import { createTheme } from '@material-ui/core/styles'
import { alpha } from '@material-ui/core/styles/colorManipulator'

import {
  alertWarning,
  background,
  boldFont,
  bolderFont,
  border,
  buttonLargeFontSize,
  disabled,
  error,
  extraSmallFontSize,
  fontColor,
  largeFontSize,
  lg,
  mainFontFamily,
  md,
  mediumFontSize,
  primary,
  regularFont,
  secondary,
  secondaryBackground,
  secondaryFontFamily,
  secondaryText,
  sm,
  smallFontSize,
  xs,
  black300,
  black400,
  infoColor,
  white,
  primaryLite,
  primaryActive,
  light,
  gray500,
  primary200,
  grey400,
} from './variables'

const palette = {
  primary: {
    main: primary,
  },
  secondary: {
    main: secondary,
  },
  error: {
    main: error,
  },
  success: {
    main: secondary,
  },
  contrastThreshold: 3,
  tonalOffset: 0.2,
}

// see https://material-ui-next.com/customization/themes/
// see https://github.com/mui-org/material-ui/blob/v1-beta/src/styles/createMuiTheme.js
const theme = createTheme({
  typography: {
    fontFamily: mainFontFamily,
    useNextVariants: true,
  },

  overrides: {
    MuiSkeleton: {
      root: {
        backgroundColor: 'rgba(var(--Mlight-darkest),1) !important',
      },
    },
    MuiButton: {
      label: {
        lineHeight: '1',
        fontSize: largeFontSize,
        fontWeight: regularFont,
      },
      root: {
        fontFamily: secondaryFontFamily,
        letterSpacing: '0.9px',
        '&$disabled': {
          color: disabled,
        },
        color: disabled,
        textTransform: 'none',
        borderRadius: sm,
      },
      contained: {
        boxShadow: '1px 2px 10px 0 rgba(var(--Mprimary-darkest),.6)',
      },
      containedPrimary: {
        backgroundColor: primary,
        color: white,
        '&:hover': {
          backgroundColor: primaryLite,
        },
        '&:active': {
          backgroundColor: primaryActive,
        },
      },
      containedSecondary: {
        backgroundColor: error,
        '&:hover': {
          backgroundColor: '#d4d5d3',
        },
      },
      outlinedPrimary: {
        border: `2px solid ${primary}`,
        '&:hover': {
          border: `2px solid ${primary}`,
        },
      },
      sizeLarge: {
        padding: `${md} ${lg}`,
        minHeight: '52px',
        fontSize: buttonLargeFontSize,
      },
      sizeSmall: {
        minWidth: '130px',
        fontSize: smallFontSize,
      },
      textSecondary: {
        '&:hover': {
          borderRadius: '3px',
        },
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: light,
        color: fontColor,
      },
      rounded: {
        borderRadius: sm,
      },
    },
    MuiStepper: {
      root: {
        padding: `${lg} 0 0 15px`,
        background: 'transparent',
      },
    },
    MuiIconButton: {
      root: {
        padding: '0',
      },
    },
    MuiChip: {
      root: {
        fontFamily: secondaryFontFamily,
      },
    },
    MuiStepIcon: {
      root: {
        fontSize: '22px',
        color: `${secondaryText} !important`,
      },
      completed: {
        color: `${secondary} !important`,
      },
      active: {
        color: `${secondary} !important`,
        fontWeight: boldFont,
      },
    },
    MuiStepContent: {
      root: {
        borderLeft: `1px solid ${secondaryText}`,
      },
    },
    MuiTypography: {
      body1: {
        fontFamily: secondaryFontFamily,
        letterSpacing: '-0.5px',
        fontSize: mediumFontSize,
      },
      body2: {
        fontFamily: secondaryFontFamily,
      },
    },
    MuiFormControl: {
      root: {
        width: '100%',
      },
    },
    MuiInputBase: {
      root: {
        color: fontColor,
      },
    },
    MuiFormLabel: {
      root: {
        color: fontColor,
      },
    },

    MuiInputLabel: {
      outlined: {
        '&$error': {
          color: error,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          borderColor: grey400,
        },
        '&:hover $notchedOutline': {
          borderColor: gray500,
        },
      },
    },

    MuiFilledInput: {
      root: {
        backgroundColor: grey400,
        '&:hover': {
          backgroundColor: gray500,
        },
        '&$focused': {
          backgroundColor: gray500,
        },
      },
      underline: {
        '&::before': {
          borderBottomStyle: 'solid',
          borderBottomWidth: '2px !important',
          borderBottom: `2px solid rgb(var(--Mprimary)) !important`,
        },
        '&::after': {
          borderBottom: `2px solid rgb(var(--Mprimary-darkest)) !important`,
        },
        '&.isValid::before': {
          borderBottomColor: `rgb(var(--Mgreen)) !important`,
        },
        '&.isInvalid::after': {
          borderBottomColor: `${error} !important`,
        },
        '&.isValid::after': {
          display: 'none',
        },
      },
    },

    MuiStepLabel: {
      label: {
        textAlign: 'left',
        color: secondary,
        '&$active': {
          color: primary,
        },
        '&$completed': {
          color: 'rgb(var(--Mtxt))',
        },
      },
    },
    MuiSvgIcon: {
      colorSecondary: {
        color: secondaryText,
      },
    },
    MuiSnackbar: {
      root: {
        maxWidth: '100%',
        width: '340px',
      },
    },
    MuiSnackbarContent: {
      root: {
        borderRadius: `${sm} !important`,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        padding: '20px',
        width: '100%',
        boxShadow: 'none',
      },
      message: {
        color: 'rgb(var(--Mwhite))',
        flexGrow: '1',
        fontSize: '14px',
        lineHeight: '1.43',
        padding: '0 10px 0 0',
        '& > span': {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          overflowX: 'hidden',
          overflowY: 'auto',
          maxHeight: '160px',
          wordBreak: 'break-word',
          '& > img': {
            display: 'block',
            marginRight: '13px',
          },
        },
      },
      action: {
        paddingLeft: '0',
        '& > button': {
          color: 'rgb(var(--Mtxt))',
        },
      },
    },
    MuiTabs: {
      indicator: {
        backgroundColor: primary + '!important',
      },
    },
    MuiTab: {
      root: {
        fontFamily: secondaryFontFamily,
        fontWeight: 'normal',
        fontSize: extraSmallFontSize,

        '&$selected': {
          fontWeight: boldFont,
          backgroundColor: gray500,
          '& p': {
            color: primary + '!important',
          },
        },
        '@media (min-width: 960px)': {
          fontSize: extraSmallFontSize, // override material-ui media query
        },
      },
    },
    MuiTable: {
      root: {
        backgroundColor: 'rgb(var(--Mgray-lightest)) !important',
        boxShadow: 'none !important',
        border: '1px solid rgb(var(--Mgray))',
      },
    },
    MuiTableContainer: {
      root: {
        marginLeft: '-10px',
        marginRight: '-10px',
        marginTop: '-10px',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '10px',
      },
    },
    MuiTablePagination: {
      root: {
        backgroundColor: 'rgb(var(--Mgray-lightest)) !important',
        boxShadow: 'none !important',
        border: '1px solid rgb(var(--Mgray))',
      },
      toolbar: {
        paddingRight: '15px',
        '& > span:nth-child(2)': {
          order: 1,
        },
      },
      selectIcon: {
        height: '100%',
        top: '0px',
      },
      caption: {
        color: fontColor,
        fontFamily: secondaryFontFamily,
        fontSize: mediumFontSize,
        order: '2',
      },
      input: {
        color: fontColor,
        order: '2',
        width: '60px',
      },
      select: {
        minWidth: lg,
        paddingRight: '30',
        backgroundColor: 'rgb(var(--Mgray-lightest)) !important',
      },
      actions: {
        color: fontColor,
        order: '4',
      },
    },
    MuiTableSortLabel: {
      root: {
        fontSize: extraSmallFontSize,
      },
      active: {
        fontWeight: boldFont,
        color: 'rgb(var(--Mprimary)) !important',
      },
    },
    MuiTableRow: {
      hover: {
        backgroundColor: 'rgb(var(--Mgray)) !important',
      },
    },
    MuiTableCell: {
      root: {
        borderBottomWidth: '1px',
        borderColor: 'rgb(var(--Mgray)) !important',
        fontFamily: secondaryFontFamily,
        fontSize: mediumFontSize,
      },
      head: {
        color: 'rgb(var(--Mtxt))',
        letterSpacing: '1px',
        textTransform: 'uppercase',
      },
      body: {
        color: 'rgb(var(--Mtext))',
        fontWeight: 'normal',
        letterSpacing: 'normal',
        overflow: 'hidden',
        paddingBottom: xs,
        paddingTop: xs,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    MuiBackdrop: {
      root: {
        backdropFilter: 'blur(1px)',
        backgroundColor: 'rgba(228, 232, 241, 0.75)',
        top: '0',
      },
    },
    MuiMenu: {
      paper: {
        backgroundColor: 'rgb(var(--Mgray-lightest))',
      },
    },
    MuiMenuItem: {
      root: {
        color: 'rgb(var(--Mtext))',
        fontFamily: secondaryFontFamily,
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 'auto',
      },
    },
    MuiListItemText: {
      primary: {
        color: fontColor,
        fontFamily: secondaryFontFamily,
        fontSize: mediumFontSize,
        fontWeight: bolderFont,
      },
      secondary: {
        color: fontColor,
        fontFamily: secondaryFontFamily,
        fontSize: smallFontSize,
      },
    },
    MuiCheckbox: {
      colorSecondary: {
        '&$disabled': {
          color: alpha(secondary, 0.5),
        },
      },
    },

    MuiFormControlLabel: {
      label: {
        '&$disabled': {
          color: primary,
        },
      },
    },
    MuiAlert: {
      root: {
        color: fontColor,
        alignItems: 'center',
      },
      standardWarning: {
        backgroundColor: alertWarning,
      },
      standardInfo: {
        backgroundColor: infoColor,
        borderRadius: '8px',
        '& svg': {
          color: secondary,
        },
      },
      icon: {
        '& > svg': {
          width: md,
          height: md,
        },
      },
    },
    MuiAlertTitle: {
      root: {
        color: fontColor,
        fontSize: md,
        margin: 0,
      },
    },
  },
  palette,
} as any)

export default theme

export const DropdownListTheme = {
  ...theme,
  overrides: {
    ...theme.overrides,
    MuiPaper: {
      root: {
        marginTop: '10px',
        backgroundColor: 'rgba(var(--Mgray-lightest), 1)',
      },
      elevation0: {
        boxShadow: 'none',
      },
      rounded: {
        borderRadius: xs,
      },
    },
    MuiList: {
      padding: {
        paddingBottom: '0',
        paddingTop: '0',
      },
    },
    MuiListItem: {
      root: {
        borderBottom: `1px solid ${border}`,
        '&:last-child': {
          borderBottom: 'none',
        },
        boxSizing: 'border-box',
      },
      button: {
        '&:hover': {
          backgroundColor: 'rgba(var(--Mgray-lighter), 1)',
        },
      },
    },
  },
}
